/*
 * IMPORTS
 */
import React from 'react' // Npm: React.js library.
import PropTypes from 'prop-types' // Npm: React.js library.
import _ from 'underscore' // Npm: Utility module.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { HiPlay, HiStop } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useBreakpointValue,
  useStyleConfig
} from '@chakra-ui/react' // Npm: A simple, modular and accessible component library for React.js.
import './index.css'


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'


/*
 * GRAPHS
 */
import VendorAccountConnectedQuery from './__query__/index.vendorAccount.connected.query'
import SmppActiveInActiveMutation from './__mutation__/index.smpp.activeInActive.mutation'


/*
 * OBJECTS
 */
function Index({ title, isConnect }) {
  // Const assignment.
  const _successFlags = ['READ_SUCCESSFUL']

  // Hook assignment.
  const [playIsLoading, setPlayIsLoading] = React.useState([])
  const [isCacheClearLoading, setIsCacheClearLoading] = React.useState([])
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 50,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [MutationSmppActiveInActive] = useMutation(SmppActiveInActiveMutation)
  const _QueryVendorAccountConnected = useQuery(VendorAccountConnectedQuery, {
    'variables': { 'isActive': Boolean(isConnect) },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': 7000
  })

  const _styles = useStyleConfig('Card')
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)

  // Data assignment.
  if (!_QueryVendorAccountConnected.loading && 0 < _QueryVendorAccountConnected.data?.VendorAccountConnected?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryVendorAccountConnected?.data?.VendorAccountConnected?.length || (0 < _QueryVendorAccountConnected?.data?.VendorAccountConnected?.length && !_.every(_.pluck(_QueryVendorAccountConnected?.data?.VendorAccountConnected, 'status'), j => _successFlags.includes(j)))) _isFirstLoadCompleted.current = false

  // No separate functions - inline logic like original

  // Define columns
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 0.5,
      'minWidth': 50,
      'cellClassName': 'cell-font'
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'action',
      'headerName': 'Action',
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1.2,
      'minWidth': 160,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => (
        <Flex justify='center' align='center'>
          <Button
            my='0px'
            height='10px'
            _hover={{ 'bg': 'none' }}
            _active={{ 'bg': 'none' }}
            bg='none'
            onClick={async () => {
              // If smpp is active then deactivate it.
              setPlayIsLoading(i => [...i, row.smppId])

              // If given smpp is active then deactivate it else activate it.
              if (row.isActive) {
                // Mutation to deactivate the smpp.
                const MutationSmppActiveInActiveResponse = await MutationSmppActiveInActive({ 'variables': { 'smppId': row.smppId, 'isActive': false } })

                // Show toast.
                toast(MutationSmppActiveInActiveResponse?.data?.SmppActiveInActive?.message ?? 'Successfully destroyed the smpp.')

                // Remove the smpp id from the loading array.
                return setPlayIsLoading(i => i.filter(j => j !== row.smppId))
              }

              // Activate the smpp.
              const MutationSmppActiveInActiveResponse = await MutationSmppActiveInActive({ 'variables': { 'smppId': row.smppId, 'isActive': true } })

              // If smpp is inactive then activate it.
              toast(MutationSmppActiveInActiveResponse?.data?.SmppActiveInActive?.message ?? 'Successfully booted the smpp.')

              // Remove the smpp id from the loading array.
              return setPlayIsLoading(i => i.filter(j => j !== row.smppId))
            }}
            px='0'
            py='0'
            color='green.400'>
            {playIsLoading.includes(row.smppId) ? (
              <Spinner size='sm' />
            ) : row.isActive ? (
              <HiStop size={17} />
            ) : (
              <HiPlay size={17} />
            )}
          </Button>
          <Button
            my='0px'
            height='10px'
            pl='10px'
            borderLeft='2px solid var(--chakra-colors-gray-300)'
            borderRadius={0}
            _hover={{ 'bg': 'none' }}
            _active={{ 'bg': 'none' }}
            bg='none'
            onClick={async () => {
              // If smpp is active then deactivate it.
              setIsCacheClearLoading(i => [...i, row.smppId])

              // Activate the smpp.
              const MutationSmppActiveInActiveResponse = await MutationSmppActiveInActive({ 'variables': { 'smppId': row.smppId, 'isActive': false, 'clearSmppBuild': true } })

              // If smpp is inactive then activate it.
              toast(MutationSmppActiveInActiveResponse?.data?.SmppActiveInActive?.message ?? 'Successfully cleared the smpp cache.')

              // Remove the smpp id from the loading array.
              return setIsCacheClearLoading(i => i.filter(j => j !== row.smppId))
            }}
            px='0'
            py='0'
            color='tomato'>
            {isCacheClearLoading.includes(row.smppId) ? (
              <Text fontSize={12} fontWeight={700}>PLEASE WAIT</Text>
            ) : (
              <Text fontSize={12} fontWeight={700}>CLEAN CACHE</Text>
            )}
          </Button>
        </Flex>
      )
    },
    {
      'field': 'displayName',
      'headerName': 'Account',
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1.5,
      'minWidth': 150,
      'filterable': true,
      'sortable': false,
      'cellClassName': 'cell-font'
    },
    {
      'field': 'status',
      'headerName': 'Status',
      'align': 'center',
      'headerAlign': 'center',
      'flex': 0.8,
      'minWidth': 90,
      'disableColumnMenu': true,
      'renderCell': () => (
        <Button
          width='80.65px'
          height='22px'
          color={isConnect ? 'rgb(32, 32, 32)' : 'rgba(217, 11, 11, 1)'}
          fontSize='13px'
          background={isConnect ? 'rgba(46, 204, 113, 0.3)' : 'rgba(255, 242, 242, 1)'}>
          {isConnect ? '.Online' : '.Offline'}
        </Button>
      )
    },
    {
      'field': 'mode',
      'headerName': 'Mode',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 0.7,
      'minWidth': 70,
      'cellClassName': 'cell-font'
    }
  ], [playIsLoading, isCacheClearLoading, isConnect])

  // Define rows
  const rows = React.useMemo(() => {
    return _.compact(_QueryVendorAccountConnected?.data?.VendorAccountConnected?.map((item, index) => {
      if (_.isEmpty(item?.Smpp?.id) || item.status !== 'READ_SUCCESSFUL') return null

      return {
        'id': item?.Smpp?.id || String.random(9),
        'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
        'smppId': item?.Smpp?.id,
        'displayName': item?.displayName,
        'isActive': item?.Smpp?.isActive,
        'mode': item?.Smpp?.mode,
        'status': item?.status
      }
    }))
  }, [_QueryVendorAccountConnected?.data?.VendorAccountConnected, paginationModel.page])

  // Return component.
  return (
    <Box
      __css={_styles}
      justifyContent='center'
      align='center'
      display='flex'
      direction='column'
      height='100%'
      w='100%'
      p={_isCurrentViewMobile ? '12px' : '22px'}
      gap={_isCurrentViewMobile ? '12px' : '22px'}
      fontSize={['clamp(12px, 1.5vw, 15px)']}
      boxShadow='14px 17px 40px 4px rgba(112, 144, 176, 0.17)'>
      <Flex justify='space-between' alignItems='center' width='100%'>
        <Text
          color='rgba(43,54,116,1)'
          fontWeight='700'
          fontSize={{ 'base': 'sm', 'md': 'md' }}
          textAlign='left'>
          {title ?? 'NOT CONNECTED VENDOR ACCOUNTS'}
        </Text>
      </Flex>
      <Box width='100%' height='450px' overflowX='auto' overflowY='hidden'>
        <DataGrid
          key={`${paginationModel.page}-${paginationModel.pageSize}`}
          rows={rows}
          columns={columns}
          rowHeight={45}
          paginationMode='client'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[50, 100]}
          sx={{
            '& .MuiDataGrid-root': {
              width: '100%',
              minWidth: 'fit-content'
            },
            '& .MuiDataGrid-cell': {
              display: 'flex !important',
            }
          }}
          slots={{
            'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
            'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
            'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
          }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
          loading={_QueryVendorAccountConnected?.loading}

        />
      </Box>
    </Box>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isConnect': PropTypes.bool,
  'title': PropTypes.string
}


/*
 * EXPORTS
 */
export default Index
